/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The pieces of the automation canvas: the step palette (drag from it, or
 * click to add), the flow's step cards, the connectors with their "+" drop
 * zones, and the End chip. Drag and drop is the browser's own (HTML5), so no
 * new library; every drag has a click or keyboard equivalent.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Copy, GripVertical, Plus, Trash2 } from "lucide-react";
import { v2 as t } from "styles/tokens/v2";
import { ACTIONS, DRAG_TYPE, LOGIC, TONES, TRIGGERS } from "./stepKinds";

function IconTile({ icon: Icon, tone, size = 34 }) {
  return (
    <Box aria-hidden="true" sx={{ width: size, height: size, flexShrink: 0, borderRadius: "9px", display: "grid", placeItems: "center", bgcolor: tone.bg, color: tone.fg }}>
      <Icon size={size * 0.5} />
    </Box>
  );
}
IconTile.propTypes = { icon: PropTypes.elementType.isRequired, tone: PropTypes.object.isRequired, size: PropTypes.number };
IconTile.defaultProps = { size: 34 };

// ---------------------------------------------------------------- palette

function PaletteItem({ icon, tone, label, payload, onAdd, disabled, hint }) {
  const item = (
    <ButtonBase
      draggable={!disabled}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = "copyMove";
      }}
      onClick={onAdd}
      disabled={disabled}
      aria-label={disabled ? `${label} (coming next)` : `Add ${label}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        justifyContent: "flex-start",
        px: 0.75,
        py: 0.625,
        borderRadius: "8px",
        bgcolor: "transparent",
        fontFamily: "inherit",
        cursor: disabled ? "default" : "grab",
        opacity: disabled ? 0.55 : 1,
        "& .grip": { opacity: 0, transition: "opacity 120ms" },
        "&:hover": disabled ? {} : { bgcolor: t.hover, "& .grip": { opacity: 1 } },
        "&:active": { cursor: disabled ? "default" : "grabbing" },
        "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 1 },
      }}
    >
      <IconTile icon={icon} tone={tone} size={28} />
      <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: t.text, textAlign: "left" }}>
        {label}
      </Typography>
      {disabled ? (
        <Typography component="span" sx={{ fontSize: 11, color: t.text3 }}>
          Soon
        </Typography>
      ) : (
        <Box component="span" className="grip" sx={{ display: "inline-flex" }}>
          <GripVertical size={15} color={t.text3} aria-hidden="true" />
        </Box>
      )}
    </ButtonBase>
  );
  return hint ? (
    <Tooltip title={hint} placement="right">
      <span>{item}</span>
    </Tooltip>
  ) : (
    item
  );
}
PaletteItem.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  payload: PropTypes.object,
  onAdd: PropTypes.func,
  disabled: PropTypes.bool,
  hint: PropTypes.string,
};
PaletteItem.defaultProps = { payload: {}, onAdd: undefined, disabled: false, hint: "" };

const groupLabelSx = { fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.text3, mt: 1.75, mb: 0.25, px: 0.75 };

// engineReady: the backend runs flows (branches, schedules); until it does
// those stay "coming next" and Condition edits the gate only.
export function Palette({ onTrigger, onCondition, onAdd, engineReady }) {
  return (
    <Box component="aside" aria-label="Steps" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography sx={{ ...groupLabelSx, mt: 0 }}>Triggers</Typography>
      {TRIGGERS.map((tr) => (
        <PaletteItem
          key={tr.key}
          icon={tr.icon}
          tone={TONES.trigger}
          label={tr.label}
          disabled={tr.comingNext && !engineReady}
          hint={tr.comingNext && !engineReady ? "Scheduled automations are coming next." : "Click to use as the trigger."}
          onAdd={() => onTrigger(tr.key)}
        />
      ))}
      <Typography sx={groupLabelSx}>Logic</Typography>
      {engineReady ? (
        <PaletteItem icon={LOGIC.condition.icon} tone={TONES.condition} label="Condition" payload={{ source: "palette", kind: "condition" }} hint="Drag into the flow: it splits into Yes and No branches." onAdd={() => onAdd({ kind: "condition" })} />
      ) : (
        <PaletteItem icon={LOGIC.condition.icon} tone={TONES.condition} label="Condition" hint="Filters that decide whether the automation runs. Yes/No branches are coming next." onAdd={onCondition} />
      )}
      <PaletteItem icon={LOGIC.wait.icon} tone={TONES.wait} label="Wait" payload={{ source: "palette", kind: "wait" }} hint="Drag into the flow: steps below it run that much later." onAdd={() => onAdd({ kind: "wait" })} />
      <Typography sx={groupLabelSx}>Actions</Typography>
      {ACTIONS.map((a) => (
        <PaletteItem key={a.type} icon={a.icon} tone={TONES.action} label={a.label} payload={{ source: "palette", kind: "action", type: a.type }} onAdd={() => onAdd({ kind: "action", type: a.type })} />
      ))}
    </Box>
  );
}
Palette.propTypes = { onTrigger: PropTypes.func.isRequired, onCondition: PropTypes.func.isRequired, onAdd: PropTypes.func.isRequired, engineReady: PropTypes.bool };
Palette.defaultProps = { engineReady: false };

// ---------------------------------------------------------------- flow

export function FlowCard({ icon, tone, kindLabel, title, sub, selected, invalid, onSelect, draggableId, onDuplicate, onDelete, badge, width, highlighted, onDropAfter }) {
  const [over, setOver] = useState(false);
  const dropProps = onDropAfter
    ? {
        onDragOver: (e) => {
          if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
          e.preventDefault();
          e.stopPropagation();
          if (!over) setOver(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setOver(false);
        },
        onDrop: (e) => {
          setOver(false);
          const raw = e.dataTransfer.getData(DRAG_TYPE);
          if (!raw) return;
          e.preventDefault();
          e.stopPropagation();
          try {
            onDropAfter(JSON.parse(raw));
          } catch (error) {
            // A malformed drag is ignored.
          }
        },
      }
    : {};
  return (
    <Box
      {...dropProps}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
        if ((e.key === "Delete" || e.key === "Backspace") && onDelete) {
          e.preventDefault();
          onDelete();
        }
      }}
      draggable={Boolean(draggableId)}
      onDragStart={
        draggableId
          ? (e) => {
              e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ source: "canvas", id: draggableId }));
              e.dataTransfer.effectAllowed = "move";
            }
          : undefined
      }
      sx={{
        position: "relative",
        width,
        maxWidth: "100%",
        display: "flex",
        gap: 1.5,
        p: 1.75,
        borderRadius: "12px",
        bgcolor: t.surface,
        border: `${selected ? 2 : 1}px solid ${invalid ? "#D92D20" : selected ? t.primary : highlighted ? "#16A34A" : t.border}`,
        // A step dragged over a card goes in just below it.
        ...(over && { borderBottom: `3px solid ${t.primary}` }),
        // A test run's path is ringed in green.
        boxShadow: selected ? `0 0 0 4px rgba(21,101,200,0.12)` : highlighted ? "0 0 0 4px rgba(22,163,74,0.16)" : t.shadow,
        m: selected ? 0 : "1px",
        cursor: draggableId ? "grab" : "pointer",
        textAlign: "left",
        "&:hover .card-tools": { opacity: 1 },
        "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 2 },
      }}
    >
      <IconTile icon={icon} tone={tone} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: tone.fg }}>{kindLabel}</Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.text, lineHeight: 1.3, mt: 0.25 }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 13, color: invalid ? "#B42318" : t.text2, mt: 0.25, overflowWrap: "anywhere" }}>{sub}</Typography>}
        {badge}
      </Box>
      {(onDuplicate || onDelete) && (
        <Box className="card-tools" sx={{ display: "flex", flexDirection: "column", gap: 0.25, opacity: selected ? 1 : 0, transition: "opacity 120ms" }}>
          {onDuplicate && (
            <Tooltip title="Duplicate">
              <IconButton size="small" aria-label="Duplicate step" onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
                <Copy size={15} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" aria-label="Delete step" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                <Trash2 size={15} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
}
FlowCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.object.isRequired,
  kindLabel: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  sub: PropTypes.node,
  selected: PropTypes.bool,
  invalid: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  draggableId: PropTypes.string,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func,
  badge: PropTypes.node,
  width: PropTypes.number,
  highlighted: PropTypes.bool,
  onDropAfter: PropTypes.func,
};
FlowCard.defaultProps = { sub: null, selected: false, invalid: false, draggableId: null, onDuplicate: null, onDelete: null, badge: null, width: 340, highlighted: false, onDropAfter: null };

export const Connector = ({ height = 18 }) => <Box aria-hidden="true" sx={{ width: "2px", height, bgcolor: t.border2 }} />;
Connector.propTypes = { height: PropTypes.number };
Connector.defaultProps = { height: 18 };

/** The "+" between two steps: a drop target, and a menu to add a step there. */
export function DropZone({ index, onDrop, onAdd, where, allowCondition }) {
  const [over, setOver] = useState(false);
  const [anchor, setAnchor] = useState(null);
  return (
    <Box
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const raw = e.dataTransfer.getData(DRAG_TYPE);
        if (!raw) return;
        e.preventDefault();
        e.stopPropagation();
        try {
          onDrop(JSON.parse(raw), index);
        } catch (error) {
          // A malformed drag is ignored.
        }
      }}
      // Nothing moves or changes shape while a drag hovers here: swapping the
      // element under the pointer makes the browser drop the drag.
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 6 }}
    >
      <Connector height={14} />
      <Tooltip title="Add a step here, or drop one">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`Add a step ${where || `at position ${index + 1}`}`}
          sx={{
            width: 26,
            height: 26,
            borderRadius: "13px",
            boxShadow: over ? "0 0 0 6px rgba(21,101,200,0.15)" : "none",
            border: `1.5px ${over ? "dashed" : "solid"} ${over ? t.primary : t.border2}`,
            bgcolor: over ? "rgba(21,101,200,0.08)" : t.surface,
            color: over ? t.primary : t.text2,
            fontFamily: "inherit",
            fontSize: 12.5,
            fontWeight: 600,
            transition: "box-shadow 120ms",
            "&:hover": { borderColor: t.primary, color: t.primary },
          }}
        >
          <Plus size={15} style={{ pointerEvents: "none" }} />
        </ButtonBase>
      </Tooltip>
      <Connector height={14} />
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => { setAnchor(null); onAdd({ kind: "wait" }, index); }}>Wait</MenuItem>
        {allowCondition && <MenuItem onClick={() => { setAnchor(null); onAdd({ kind: "condition" }, index); }}>Condition (Yes / No)</MenuItem>}
        {ACTIONS.map((a) => (
          <MenuItem key={a.type} onClick={() => { setAnchor(null); onAdd({ kind: "action", type: a.type }, index); }}>
            {a.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
DropZone.propTypes = { index: PropTypes.number.isRequired, onDrop: PropTypes.func.isRequired, onAdd: PropTypes.func.isRequired, where: PropTypes.string, allowCondition: PropTypes.bool };
DropZone.defaultProps = { where: "", allowCondition: false };

export const FlowChip = ({ children }) => (
  <Box sx={{ px: 1.5, height: 28, display: "inline-flex", alignItems: "center", borderRadius: "14px", border: `1px solid ${t.border}`, bgcolor: t.surface, fontSize: 13, fontWeight: 600, color: t.text2 }}>
    {children}
  </Box>
);
FlowChip.propTypes = { children: PropTypes.node.isRequired };
